import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { FontSize12, FontWeightSemiBold } from 'autogen/design-tokens/tokens';
import type { CheckoutBasketModelsSubscriptionInfoViewModel } from 'autogen/swagger/all';
import { AddedAs, AsSubscription } from 'autogen/translation-keys/trans-website-basket-subscription';
import InlineButton from 'components/generic/InlineButton';
import { Typography } from 'components/generic/typography';
import QuickView, { useQuickView } from 'components/quickView';
import { fetchHTML } from 'modules/helpers/fetch';
import { formatReact } from 'modules/helpers/strings';
import ReactLoadingSpinner from 'modules/loadingSpinner';
import useGA4Tracking from 'modules/tracking/GA4';

const StyledInlineButton = styled(InlineButton)`
    font-size: ${FontSize12};
    font-weight: ${FontWeightSemiBold};
`;

export default function SubscriptionInformation({
    model: { infoLinkAction },
    productId
}: {
    model: CheckoutBasketModelsSubscriptionInfoViewModel;
    productId: number;
}) {
    const { t } = useTranslation();
    const quickViewId = `SubscriptionInformationQuickView-${productId}`;
    const { toggle } = useQuickView(quickViewId);
    const { refetch, data, isLoading } = useQuery(['subscriptionInformation'], () => fetchHTML(infoLinkAction.url), {
        enabled: false
    });
    const GA4Tracking = useGA4Tracking();

    return (
        <>
            <QuickView quickViewId={quickViewId}>
                {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
                {!data || isLoading ? <ReactLoadingSpinner /> : <div dangerouslySetInnerHTML={{ __html: data }} />}
            </QuickView>
            <Typography.Body as="span" size="S" strong>
                {formatReact(t(AddedAs))(
                    <StyledInlineButton
                        type="button"
                        onClick={() => {
                            refetch();
                            toggle(true);
                            GA4Tracking({
                                eventName: 'read_more',
                                category: 'ecommerce',
                                eventParams: [{ param_name: 'read_more_type', param_value: 'subscription_terms' }]
                            });
                        }}
                    >
                        {t(AsSubscription)}
                    </StyledInlineButton>
                )}
            </Typography.Body>
        </>
    );
}
